// Chemin: C:\gestion-copro\src\pages\admin\AffairesPage.tsx
// Session 17 : Ajout infobulle d'aide
// Session 58 : Correction bug coproprietaireId → coproprieteIds[0]
// Session 44 : Harmonisation charte graphique (icônes)

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/layout/AdminLayout';
import HelpBanner from '../../components/ui/HelpBanner';
import {
  Affaire,
  StatutAffaire,
  TypeAffaire,
  TYPE_AFFAIRE_LABELS,
  STATUT_AFFAIRE_LABELS,
  STATUT_AFFAIRE_COLORS,
  PRIORITE_AFFAIRE_LABELS,
  PRIORITE_AFFAIRE_COLORS
} from '../../types/affaire.types';
import { getAffairesByCopropriete, countAffairesByStatut } from '../../services/affaire.service';

export default function AffairesPage() {
  const { userProfile } = useAuth();
  const [affaires, setAffaires] = useState<Affaire[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filtres
  const [filtreStatut, setFiltreStatut] = useState<StatutAffaire | 'tous'>('tous');
  const [filtreType, setFiltreType] = useState<TypeAffaire | 'tous'>('tous');
  const [recherche, setRecherche] = useState('');
  
  // Compteurs
  const [compteurs, setCompteurs] = useState<Record<StatutAffaire, number>>({
    ouverte: 0,
    en_cours: 0,
    en_attente: 0,
    cloturee: 0
  });

  const coproprieteId = userProfile?.coproprieteIds?.[0];

  useEffect(() => {
    if (!coproprieteId) return;
    
    const loadData = async () => {
      try {
        setLoading(true);
        const [affairesData, compteursData] = await Promise.all([
          getAffairesByCopropriete(coproprieteId),
          countAffairesByStatut(coproprieteId)
        ]);
        setAffaires(affairesData);
        setCompteurs(compteursData);
      } catch (err) {
        console.error('Erreur chargement affaires:', err);
        setError('Impossible de charger les affaires');
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [coproprieteId]);

  // Filtrage des affaires
  const affairesFiltrees = affaires.filter(affaire => {
    // Filtre statut
    if (filtreStatut !== 'tous' && affaire.statut !== filtreStatut) {
      return false;
    }
    
    // Filtre type
    if (filtreType !== 'tous' && affaire.type !== filtreType) {
      return false;
    }
    
    // Recherche texte
    if (recherche) {
      const searchLower = recherche.toLowerCase();
      return (
        affaire.titre.toLowerCase().includes(searchLower) ||
        affaire.numero.toLowerCase().includes(searchLower) ||
        affaire.description.toLowerCase().includes(searchLower)
      );
    }
    
    return true;
  });

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'Non renseigné';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('fr-FR');
  };

  if (!coproprieteId) {
    return (
      <AdminLayout>
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">
            Aucune copropriété associée à votre compte. 
            Veuillez d'abord créer ou rejoindre une copropriété.
          </p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* En-tête */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Affaires & Dossiers</h1>
            <p className="text-gray-600 mt-1">
              Gérez vos dossiers transverses : litiges, sinistres, travaux, projets
            </p>
          </div>
          <Link
            to="/admin/affaires/nouvelle"
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            + Nouvelle affaire
          </Link>
        </div>

        {/* Aide contextuelle */}
        <HelpBanner
          storageKey="help_dismissed_affaires"
          message="Une affaire regroupe signalements, documents et échanges autour d'un même sujet (litige, sinistre, projet...). Créez une affaire pour centraliser le suivi, puis rattachez-y vos documents et signalements."
        />

        {/* Compteurs par statut */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <button
            onClick={() => setFiltreStatut(filtreStatut === 'ouverte' ? 'tous' : 'ouverte')}
            className={`p-4 rounded-lg border-2 transition-colors ${
              filtreStatut === 'ouverte' 
                ? 'border-blue-500 bg-blue-50' 
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="text-2xl font-bold text-blue-600">{compteurs.ouverte}</div>
            <div className="text-sm text-gray-600">Ouvertes</div>
          </button>
          
          <button
            onClick={() => setFiltreStatut(filtreStatut === 'en_cours' ? 'tous' : 'en_cours')}
            className={`p-4 rounded-lg border-2 transition-colors ${
              filtreStatut === 'en_cours' 
                ? 'border-yellow-500 bg-yellow-50' 
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="text-2xl font-bold text-yellow-600">{compteurs.en_cours}</div>
            <div className="text-sm text-gray-600">En cours</div>
          </button>
          
          <button
            onClick={() => setFiltreStatut(filtreStatut === 'en_attente' ? 'tous' : 'en_attente')}
            className={`p-4 rounded-lg border-2 transition-colors ${
              filtreStatut === 'en_attente' 
                ? 'border-gray-500 bg-gray-50' 
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="text-2xl font-bold text-gray-600">{compteurs.en_attente}</div>
            <div className="text-sm text-gray-600">En attente</div>
          </button>
          
          <button
            onClick={() => setFiltreStatut(filtreStatut === 'cloturee' ? 'tous' : 'cloturee')}
            className={`p-4 rounded-lg border-2 transition-colors ${
              filtreStatut === 'cloturee' 
                ? 'border-green-500 bg-green-50' 
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="text-2xl font-bold text-green-600">{compteurs.cloturee}</div>
            <div className="text-sm text-gray-600">Clôturées</div>
          </button>
        </div>

        {/* Filtres et recherche */}
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Recherche */}
            <div className="flex-1">
              <input
                type="text"
                placeholder="Rechercher par titre, numéro..."
                value={recherche}
                onChange={(e) => setRecherche(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            {/* Filtre type */}
            <div>
              <select
                value={filtreType}
                onChange={(e) => setFiltreType(e.target.value as TypeAffaire | 'tous')}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="tous">Tous les types</option>
                {Object.entries(TYPE_AFFAIRE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
            
            {/* Réinitialiser */}
            {(filtreStatut !== 'tous' || filtreType !== 'tous' || recherche) && (
              <button
                onClick={() => {
                  setFiltreStatut('tous');
                  setFiltreType('tous');
                  setRecherche('');
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-900"
              >
                Réinitialiser
              </button>
            )}
          </div>
        </div>

        {/* Liste des affaires */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">{error}</p>
          </div>
        ) : affairesFiltrees.length === 0 ? (
          <div className="bg-gray-50 rounded-lg p-8 text-center">
            <p className="text-gray-600">
              {affaires.length === 0 
                ? 'Aucune affaire créée. Commencez par créer votre première affaire.'
                : 'Aucune affaire ne correspond aux filtres sélectionnés.'
              }
            </p>
            {affaires.length === 0 && (
              <Link
                to="/admin/affaires/nouvelle"
                className="inline-block mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
              >
                Créer une affaire
              </Link>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Affaire
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Priorité
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Éléments
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {affairesFiltrees.map((affaire) => (
                  <tr key={affaire.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {affaire.titre}
                        </div>
                        <div className="text-sm text-gray-500">
                          {affaire.numero}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-600">
                        {TYPE_AFFAIRE_LABELS[affaire.type]}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${STATUT_AFFAIRE_COLORS[affaire.statut]}`}>
                        {STATUT_AFFAIRE_LABELS[affaire.statut]}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${PRIORITE_AFFAIRE_COLORS[affaire.priorite]}`}>
                        {PRIORITE_AFFAIRE_LABELS[affaire.priorite]}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex gap-3">
                        <span title="Signalements">
                          {affaire.signalementIds.length} sig.
                        </span>
                        <span title="Documents">
                          {affaire.documentIds.length} doc.
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(affaire.dateOuverture)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center justify-center gap-2">
                        {/* Bouton Voir */}
                        <Link
                          to={`/admin/affaires/${affaire.id}`}
                          className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                          title="Voir le détail"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </Link>
                        {/* Bouton Modifier */}
                        <Link
                          to={`/admin/affaires/${affaire.id}/modifier`}
                          className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                          title="Modifier"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
